import { takeEvery, put, select } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';

import { IResponseError } from 'ApiPackage/MainModule/interfaces/IResponseError';
import { IKnmPreview } from 'ApiPackage/SearchModule/interfaces/IKnmPreview';
import {
  getDecisionPassportById,
  getDecisionPassportByToken,
  getDecisionPassportPdfById,
  getPassportById,
  getPassportByToken,
  getPassportPdfById,
} from 'ApiPackage/SearchModule/services/search';
import { captchaSetError, captchaSetIsActive } from 'CorePackage/CaptchaModule/actions/captchaActions';
import { getCaptchaToken } from 'CorePackage/CaptchaModule/selectors/captchaSelectors';
import { MIME_PDF } from 'CorePackage/MainModule/constants/mime';
import { INTERNAL_SERVER_ERROR } from 'CorePackage/MainModule/constants/notices';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { saveFile } from 'CorePackage/MainModule/common/saveFile';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';
import { ROUTE_PORTAL } from 'CorePackage/MainModule/constants/routes';
import { IDecisionPreview } from 'ApiPackage/SearchModule/interfaces/IDecisionPreview';

import { PassportKnmActionsEnum } from '../enums/PassportKnmActionsEnum';
import {
  passportKnmSetErrorStatus,
  passportKnmGetData,
  passportKnmSetData,
  passportKnmGetDataByQr,
  passportDecisionSetData,
} from '../actions/passportKnmActions';
import { getPassportKnmData, getPassportKnmDataDecision } from '../reducers/passportKnmReducer';
import {
  ROUTE_KNM_BY_NONEXISTENT_QR_CODE,
  ROUTE_PM_BY_NONEXISTENT_QR_CODE,
} from '../../../CorePackage/MainModule/constants/routes';

function* renderError(error: IResponseError) {
  const { status } = error;
  if (status === 500) {
    yield put(captchaSetError(INTERNAL_SERVER_ERROR));
  } else {
    yield put(captchaSetError('Неверное проверочное значение'));
    yield put(captchaSetIsActive(true));
  }
  if (status) {
    yield put(passportKnmSetErrorStatus(status));
  }
}

function* passportKnmGetDataSaga(action: ActionType<typeof passportKnmGetData>) {
  function* funcTry() {
    const { id } = action.payload;
    const captchaToken: string | null = yield select(getCaptchaToken);
    if (id) {
      const data: IKnmPreview = yield getPassportById(id, captchaToken);
      if (data) {
        yield put(passportKnmSetData(data));
      }
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(err: IResponseError) {
      yield renderError(err);
    },
  });
}

function* passportKnmGetDataByQrSaga(action: ActionType<typeof passportKnmGetDataByQr>) {
  function* funcTry() {
    const { token } = action.payload;
    const data: IKnmPreview = yield getPassportByToken(token).catch(e => {
      if (e.message === 'PM_BY_QR_FORBIDDEN') {
        window.location.href = `${ROUTE_PORTAL}?mt=pm`;
      } else if (e.message === 'KNM_BY_QR_FORBIDDEN') {
        window.location.href = `${ROUTE_PORTAL}?mt=knm`;
      }
    });
    if (data) {
      yield put(passportKnmSetData(data));
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(err: IResponseError) {
      if (err.message === 'KNM_BY_QR_FORBIDDEN') {
        window.location.href = ROUTE_KNM_BY_NONEXISTENT_QR_CODE;
      } else if (err.message === 'PM_BY_QR_FORBIDDEN') {
        window.location.href = ROUTE_PM_BY_NONEXISTENT_QR_CODE;
      } else {
        yield renderError(err);
      }
    },
  });
}

function* passportKnmExportPdfSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const { id, erpId, pmErknm, version } = getPassportKnmData(state) || {};
    const captchaToken = getCaptchaToken(state);
    const isErknmOrErp = version === 'ERKNM' || version === 'ERP';
    const isPm = isErknmOrErp && pmErknm;
    if (id) {
      const doc: Blob = yield getPassportPdfById(id, captchaToken);
      const file = new Blob([doc], { type: MIME_PDF });
      saveFile(file, `${isPm ? 'pm' : 'knm'}-${erpId}.pdf`);
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(captchaSetIsActive(true));
    },
  });
}

function* passportDecisionGetDataSaga(action: ActionType<typeof passportKnmGetData>) {
  function* funcTry() {
    const { id } = action.payload;
    const captchaToken: string | null = yield select(getCaptchaToken);
    if (id) {
      const data: IDecisionPreview = yield getDecisionPassportById(id, captchaToken);
      if (data) {
        yield put(passportDecisionSetData(data));
      }
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(err: IResponseError) {
      yield renderError(err);
    },
  });
}

function* passportDecisionGetDataByQrSaga(action: ActionType<typeof passportKnmGetDataByQr>) {
  function* funcTry() {
    const { token } = action.payload;
    const data: IDecisionPreview = yield getDecisionPassportByToken(token).catch(e => {
      if (e.message === 'DECISION_BY_QR_FORBIDDEN') {
        window.location.href = `${ROUTE_PORTAL}?mt=decision`;
      }
    });
    if (data) {
      yield put(passportDecisionSetData(data));
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(err: IResponseError) {
      yield renderError(err);
    },
  });
}

function* passportDecisionExportPdfSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const { id, number } = getPassportKnmDataDecision(state) || {};
    const captchaToken = getCaptchaToken(state);
    if (id) {
      const doc: Blob = yield getDecisionPassportPdfById(id, captchaToken);
      const file = new Blob([doc], { type: MIME_PDF });
      saveFile(file, `decision-${number}.pdf`);
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(captchaSetIsActive(true));
    },
  });
}

export function* watchPassportKnmSaga() {
  yield takeEvery(PassportKnmActionsEnum.PASSPORT_KNM_GET_DATA, passportKnmGetDataSaga);
  yield takeEvery(PassportKnmActionsEnum.PASSPORT_KNM_GET_DATA_BY_QR, passportKnmGetDataByQrSaga);
  yield takeEvery(PassportKnmActionsEnum.PASSPORT_KNM_EXPORT_PDF, passportKnmExportPdfSaga);

  yield takeEvery(PassportKnmActionsEnum.PASSPORT_DECISION_GET_DATA, passportDecisionGetDataSaga);
  yield takeEvery(PassportKnmActionsEnum.PASSPORT_DECISION_GET_DATA_BY_QR, passportDecisionGetDataByQrSaga);
  yield takeEvery(PassportKnmActionsEnum.PASSPORT_DECISION_EXPORT_PDF, passportDecisionExportPdfSaga);
}
